import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import AuthService from 'services/auth'
import ScheduleService from 'services/Schedule'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import Select from 'components/Inputs/Select'
import SpecialtySelect from 'components/Inputs/SpecialtySelect'
import CheckBox from 'components/CheckBox'
import Tooltip from 'components/Tooltip'
import SyncServicesModal from '../SyncServicesModal'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import submitOnEnter from 'utils/submitOnEnter'
import editServiceValidator from 'validators/editServiceValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  FormField,
  ProductGrid,
  BundleBlock,
  Column,
  Input,
  TextArea,
  Row,
  Title,
  SubTitle,
  InfoText,
  Label,
  WarningText,
  ErrorContainer
} from './styles'

export default function EditServiceModal ({
  visible,
  professionalID,
  data: service,
  businessProducts = [],
  serviceCategories = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [professionals, setProfessionals] = useState ([])
  const [serviceBundles, setServiceBundles] = useState ([])
  const [selectedBundles, setSelectedBundles] = useState ([])
  const [errorMessage, setErrorMessage] = useState (null)
  const [showSyncDialog, setShowSyncDialog] = useState (false)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [service])

  const getData = async () => {
    try {
      setLoading (true)

      const filters = `access_level=service&status=active&deleted=false&service_id=${service.id}`

      const { data: professionals } = await cutileiApi.get (
        `/businesses/${businessId}/professionals?${filters}`, requestConfig
      )

      const { data: serviceBundles } = await cutileiApi.get (
        `/businesses/${businessId}/bundles?${filters}`, requestConfig
      )
      
      setProfessionals (professionals)
      setServiceBundles (serviceBundles)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }
  
  const calculateComissionValue = (price, comissionPercentage) => {
    return Math.round (price * (comissionPercentage / 100 || 0) * 100) / 100
  }

  const calculateComissionPercentage = (price, comissionValue) => {
    return Math.round ((comissionValue / price) * 100 * 100) / 100
  }

  const recalcutaleComissionValue = (values, setFieldValue) => {
    const comissionValue = calculateComissionValue (values.price, values.comission_percentage)
    setFieldValue ('professional_comission', comissionValue)
  }

  const toggleSyncDialog = () => setShowSyncDialog (!showSyncDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='lg' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{service.name}</DialogTitle>
        </DialogHeader>
        {loading ? <DialogBody><Loading/></DialogBody> : (
          <Formik
            validationSchema={editServiceValidator}
            initialValues={{
              'id': service.id,
              'nickname': service.nickname || service.name,
              'category': {
                label: service.category.name,
                value: service.category.id
              },
              'specialty': service.specialty_id ? { value: service.specialty_id } : null,
              'description': service.description || '',
              'duration': service.duration,
              'max_delay': service.max_delay,
              'price': service.price,
              'comission_percentage': service.comission_percentage * 100,
              'professional_comission': service.comission_percentage * service.price ?? 0,
              'business_comission': service.price - (service.comission_percentage * service.price ?? 0),
              'cutilei_service': service.cutilei_service,
              'accepts_online': service.accepts_online,
              'products': service.products ?? [],
              'professionals': []
            }}
            onSubmit={async (values, { setSubmitting }) => {
              setErrorMessage (null)
              setSubmitting (true)

              try {
                const { specialty, ...serviceValues } = values
                const data = {
                  ...serviceValues,
                  category_id: values.category.value,
                  specialty_id: specialty?.value ?? null,
                  comission_percentage: values.comission_percentage / 100
                }

                const url = professionalID
                  ? `/businesses/${businessId}/professionals/${professionalID}/services/${values.id}`
                  : `/businesses/${businessId}/services/${values.id}`

                await cutileiApi.put (url, data, requestConfig)

                if (selectedBundles.length > 0 && !professionalID) {
                  await Promise.all (selectedBundles.map (async bundle => {
                    await cutileiApi.put (`/bundles/${bundle.id}`, {
                      ...bundle,
                      services: bundle.services.map (bundleService => {
                        if (bundleService.id === values.id) {
                          return {
                            ...bundleService,
                            full_price: values.price,
                            discount_price: (
                              ((values.price * bundleService.amount) / values.price) * bundle.price
                            ) / bundleService.amount
                          }
                        }
                        return bundleService
                      })
                    }, requestConfig)
                  }))
                }

                setSubmitting (false)
                handleConfirm ({...service, ...data})
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                logApiError (error)
              }
            }}
          >
            {function EditServiceForm ({
              values,
              errors,
              touched,
              isSubmitting,
              setFieldValue,
              setFieldTouched,
              handleChange,
              handleBlur, 
              handleSubmit
            }) {
              useEffect (() => {
                recalcutaleComissionValue (values, setFieldValue)
              }, [values.price])

              useEffect (() => {
                setFieldValue ('business_comission', Math.abs (values.price - values.professional_comission))
              }, [values.price, values.comission_percentage, values.professional_comission])

              const handleAddProduct = () => {
                setFieldValue ('products', [
                  ...values.products, {
                    id: '',
                    name: '',
                    quantity_used: 1
                  }
                ])
              }

              const handleDeleteProduct = index => {
                setFieldValue (
                  'products',
                  values.products.map ((p, i) => ({...p, idx: i})).filter (s => s.idx !== index)
                )
              }

              const handleSave = professionals.length > 0 && !professionalID ? toggleSyncDialog : handleSubmit

              const serviceName = service.name.toLowerCase ()

              return (
                <>
                  <DialogBody>
                    <Form onKeyDown={submitOnEnter(handleSave, isSubmitting)}>
                      <Title style={{marginBottom: 0}}>
                        Editar serviço
                      </Title>
                      {!values.cutilei_service && (
                        <Row>
                          <FormField>
                            <Label htmlFor='nickname'>
                              Apelido
                            </Label>
                            <Input
                              id='nickname'
                              placeholder='Apelido'
                              value={values.nickname}
                              onChange={handleChange ('nickname')} 
                              onBlur={handleBlur ('nickname')}
                            />
                          </FormField>
                          <FormField>
                            <Label htmlFor='category'>
                              Categoria
                            </Label>
                            <Select
                              id='category'
                              name='category'
                              placeholder='Categoria...'
                              value={values.category}
                              options={serviceCategories}
                              isDisabled={!!professionalID}
                              onChange={(name, option) => {
                                setFieldValue (name, option)
                                setFieldValue ('specialty', null)
                              }}
                              onBlur={setFieldTouched}
                              error={errors.category}
                              touched={touched.category}
                            />
                            {touched.category && errors.category && (
                              <WarningText>
                                {errors.category}
                              </WarningText>
                            )}
                          </FormField>
                        </Row>
                      )}
                      {/* A especialidade é do salão: quem edita pelo cadastro de um profissional não a troca. */}
                      {!professionalID && (
                        <Row>
                          <FormField>
                            <Label>Especialidade</Label>
                            <SpecialtySelect
                              categoryId={values.category?.value}
                              value={values.specialty}
                              onChange={option => setFieldValue ('specialty', option)}
                            />
                          </FormField>
                        </Row>
                      )}
                      <Row style={{width: 480, alignSelf: 'center'}}>
                        <FormField>
                          <Label htmlFor='duration'>
                            Tempo do serviço (mins) {values.duration >= 60 && ` - ${
                              ScheduleService.getFormattedDuration (values.duration)
                            }`}
                          </Label>
                          <NumberInput
                            id='duration'
                            name='duration'
                            value={values.duration}
                            minValue={15}
                            stepOnly={true}
                            step={values.duration < 60 ? 15 : 30}
                            onChange={(name, value) => {
                              const newValue = values.duration === 60 && values.duration > value ? 45 : value
                              setFieldValue (name, newValue)
                            }}
                            style={{width: '100%'}}
                          />
                        </FormField>
                        <FormField>
                          <Row style={{margin: 0, gap: 3, alignItems: 'center'}}>
                            <Label htmlFor='max_delay'>
                              Tolerância de atraso (mins)
                            </Label>
                            <Tooltip
                              id='max_delay_tooltip'
                              place='bottom'
                              message={
                                <Label style={{color: '#FFFFFF', width: 300}}>
                                  A tolerância de atraso pode ser configurada da seguinte forma:<br/><br/>
                                  Serviços do salão: 5 a 20 minutos<br/>
                                  Serviços de Pé e mão Cutilei: 10 a 15 minutos<br/>
                                  Demais Serviços do Cutilei: 7 a 15 minutos<br/>
                                </Label>
                              }
                            />
                          </Row>
                          <NumberInput
                            id='max_delay'
                            name='max_delay'
                            value={values.max_delay}
                            minValue={values.cutilei_service ? (serviceName === 'pé e mão' ? 10 : 7) : 5}
                            maxValue={values.cutilei_service ? 15 : 20}
                            onChange={setFieldValue}
                            style={{width: '100%'}}
                          />
                        </FormField>
                      </Row>
                      <Row style={{alignItems: 'flex-start'}}>
                        <FormField>
                          <Label htmlFor='price'>
                            Preço (R$)
                          </Label>
                          <MoneyInput
                            id='price'
                            name='price'
                            placeholder='Preço'
                            value={values.price}
                            onValueChange={setFieldValue}
                            onBlur={handleBlur ('price')}
                            disabled={values.cutilei_service}
                          />
                          {touched.price && errors.price && (
                            <WarningText>
                              {errors.price}
                            </WarningText>
                          )}
                        </FormField>
                        <FormField>
                          <Label htmlFor='comission_percentage'>
                            Comissão (%)
                          </Label>
                          <NumberInput
                            id='comission_percentage'
                            name='comission_percentage'
                            placeholder='Comissão (%)'
                            value={values.comission_percentage}
                            minValue={0}
                            maxValue={100}
                            onChange={(name, value) => {
                              setFieldValue (name, value)
                              setFieldValue ('professional_comission', calculateComissionValue (values.price, value))
                            }}
                            onBlur={handleBlur}
                          />
                        </FormField>
                        <FormField>
                          <Label htmlFor='professional_comission'>
                            Comissão (profissional)
                          </Label>
                          <MoneyInput
                            id='professional_comission'
                            name='professional_comission'
                            placeholder='Comissão (profissional)'
                            value={values.professional_comission}
                            maxValue={values.price}
                            onValueChange={(name, value) => {
                              setFieldValue (name, value)
                              setFieldValue ('comission_percentage', calculateComissionPercentage (values.price, value))
                            }}
                            onBlur={handleBlur ('professional_comission')}
                            style={{color: '#44CF6C', fontWeight: 600}}
                          />
                        </FormField>
                        <FormField>
                          <Label htmlFor='business_comission'>
                            Valor (salão)
                          </Label>
                          <MoneyInput
                            id='business_comission'
                            name='business_comission'
                            value={values.business_comission}
                            style={{color: '#44CF6C', fontWeight: 600}}
                            disabled={true}
                          />
                        </FormField>
                      </Row>
                      {!values.cutilei_service && (
                        <>
                          <Row>
                            <FormField>
                              <TextArea 
                                placeholder='Descrição do serviço (opcional)'
                                value={values.description}
                                rows={3}
                                onChange={handleChange ('description')} 
                                onBlur={handleBlur ('description')}
                              />
                              {touched.description && errors.description && (
                                <WarningText>
                                  {errors.description}
                                </WarningText>
                              )}
                            </FormField>
                          </Row>
                          <FormField>
                            <Row style={{alignItems: 'center', marginBottom: 7}}>
                              <CheckBox
                                id='accepts_online'
                                style={{marginRight: -3}}
                                value={values.accepts_online}
                                onValueChange={value => setFieldValue ('accepts_online', value)}
                              />
                              <Label htmlFor='accepts_online' style={{margin: 0}}>
                                Permitir agendamento por link
                              </Label>
                            </Row>
                          </FormField>
                        </>
                      )}
                      {(!professionalID && values.products.length > 0) && values.products.map ((product, index) => (
                        <ProductGrid key={index}>
                          <FormField>
                            <Label>Produto</Label>
                            <Select
                              name={`products.${index}.id`}
                              placeholder='Selecionar...'
                              value={product.id ? {
                                label: product.nickname || product.name,
                                value: product.id
                              } : null}
                              options={businessProducts}
                              onChange={(name, option) => {
                                setFieldValue (name, option.value.id)
                                setFieldValue (`products.${index}.name`, option.value.nickname || option.value.name)
                              }}
                              onBlur={setFieldTouched}
                              error={errors.products?.[index]?.name}
                              touched={touched.products?.[index]?.name}
                            />
                            {touched.products?.[index]?.name && errors.products?.[index]?.name && (
                              <WarningText>
                                {errors.products?.[index]?.name}
                              </WarningText>
                            )}
                          </FormField>
                          <Row style={{margin: 0, gap: 0}}>
                            <FormField>
                              <Label>Qtd. utilizada</Label>
                              <NumberInput
                                name={`products.${index}.quantity_used`}
                                value={product.quantity_used}
                                minValue={1}
                                maxValue={999999}
                                onChange={setFieldValue}
                              />
                            </FormField>
                            {product.id && (
                              <InfoText style={{marginBottom: 8}}>
                                {businessProducts.find (p => p.value.id === product.id)?.value.content_unit}
                              </InfoText>
                            )}
                          </Row>
                          <MainButton
                            leftIcon={<FaTrash color='#FFFFFF' size={12}/>}
                            onClick={() => handleDeleteProduct (index)}
                            dangerous={true}
                            width={50}
                            style={{alignSelf: 'flex-end'}}
                          />
                        </ProductGrid>
                      ))}
                      {!professionalID && (
                        <SecondaryButton
                          label='Adicionar produto'
                          leftIcon={<FaPlus size={12} color='#FFC100'/>}
                          onClick={handleAddProduct}
                          width={180}
                          style={{marginTop: 10}}
                        />
                      )}
                      {serviceBundles.length > 0 && values.price !== service.price && !professionalID && (
                        <FormField>
                          <SubTitle>Atualizar pacotes</SubTitle>
                          <InfoText>
                            Selecione o pacote em que desejo atualizar o preço do serviço.
                          </InfoText>
                          <Column>
                            {serviceBundles.map ((bundle, index) => (
                              <BundleBlock
                                key={index}
                                selected={selectedBundles.some (b => b.id === bundle.id)}
                                onClick={() => {
                                  setSelectedBundles (selectedBundles.some (b => b.id === bundle.id)
                                    ? selectedBundles.filter (b => b.id !== bundle.id)
                                    : [...selectedBundles, bundle]
                                  )
                                }}
                              >
                                <SubTitle>
                                  {bundle.name}
                                </SubTitle>
                              </BundleBlock>
                            ))}
                          </Column>
                        </FormField>
                      )}
                      {errorMessage && (
                        <ErrorContainer>
                          <p>{errorMessage}</p>
                        </ErrorContainer>
                      )}
                      <SyncServicesModal
                        visible={showSyncDialog}
                        service={service}
                        professionals={professionals}
                        onConfirm={professionals => {
                          setFieldValue ('professionals', professionals)
                          setTimeout (() => { handleSubmit () }, 0)
                        }}
                        onClose={toggleSyncDialog}
                      />
                    </Form>
                  </DialogBody>
                  <DialogFooter>
                    <MainButton
                      label='Salvar alterações'
                      onClick={handleSave}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={180}
                      style={{marginTop: 15}}
                    />
                  </DialogFooter>
                </>
              )
            }}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
